import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import utils, { comnEnvs, comnUtils } from "@/comn/utils";
import { Button, Grid, Group, Layout, Page } from "@/comn/components";
import { useFetch, useForm, useGrid, useStore, useToast, useResource, useModal, usePage } from "@/comn/hooks";
import { APIS, URLS, SF_SURV_INFO, SG_SURV_INFO_LANG_LIST } from "./services/PtliSurvMgmtService";

/*
 * Survey Management Detail
 * !== 설문 관리 상세 ==!
 */
export const PTLI0418004Q = (): JSX.Element => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_ptli0060", "auth_ptlianyADM"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal();
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { qestnrId } = useParams();
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const langCodes = ["KO", "EN", "TZ"];

    useResource({
        defaultSchema: [],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        survInfo: useForm({
            defaultSchema: SF_SURV_INFO,
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        survInfoLangList: useGrid({
            defaultSchema: SG_SURV_INFO_LANG_LIST,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        getSurvInfo: useFetch({
            api: (data) => APIS.getSurvInfo(qestnrId),
            enabled: !!qestnrId,
            onSuccess: (data) => {
                // const survInfo = data.survInfo.content;
                // form.survInfo.setValues(survInfo);

                const { content } = data.survInfo || {};
                if (comnUtils.isEmpty(content)) {
                    modal.openModal({ content: "msg.ptl.00694" }); // 비정상 접근입니다.
                    navigate(URLS.ptli0418001q);
                    return;
                }

                form.survInfo.setValues(content);

                // 2) 다국어 필드 세팅
                const { survInfoLangList } = content;
                if (!comnUtils.isEmpty(survInfoLangList)) {
                    survInfoLangList.forEach((row: any) => {
                        const { langCd, qustnrTtle, qustnrPupsCn } = row;
                        if (!comnUtils.isEmpty(langCd)) {
                            form.survInfo.setValue(`qustnrTtle${langCd}`, qustnrTtle ?? "");
                            form.survInfo.setValue(`qustnrPupsCn${langCd}`, qustnrPupsCn ?? "");
                        }
                    });
                }
            },
            onError: (error) => {},
            showToast: true,
        }),

        deleteSurvInfo: useFetch({
            api: (qestnrId) => APIS.deleteSurvInfo(qestnrId),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.00003",
                    onCancel: () => {
                        navigate(URLS.ptli0418001q);
                    },
                });
            },
            onError: (error) => {},
            showToast: true,
        }),
    };

    const render = {};

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        deleteSurvInfo: () => {
            modal.openModal({
                content: "msg.00103",
                onConfirm: () => {
                    fetch.deleteSurvInfo.fetch(qestnrId);
                },
            });
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        fetch.getSurvInfo.fetch();
        form.survInfo.setEditable(false);
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.survInfo.schema.bsopClsfCd}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survInfo.schema.ptlTpCd}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survInfo.schema.mnPgeQustnrYn}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survInfo.schema.qustnrTtleEN}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survInfo.schema.qustnrTtleTZ}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survInfo.schema.qustnrTtleKO}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survInfo.schema.qustnrPupsCnEN}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survInfo.schema.qustnrPupsCnTZ}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survInfo.schema.qustnrPupsCnKO}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survInfo.schema.qustnrMakeInfoCn}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survInfo.schema.qustnrTrgtAcrdtYn}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survInfo.schema.reqDateRnge}></Group.Control>
                        </Group.Row>
                    </Group.Section>
                    <Group.Section hidden={true}>
                        {" "}
                        <Grid {...grid.survInfoLangList.grid} />
                    </Group.Section>
                </Group.Body>
            </Group>
            <Layout direction="row">
                <Layout.Left>
                    <Button
                        role="list"
                        onClick={() => {
                            navigate(URLS.ptli0418001q);
                        }}
                    ></Button>
                </Layout.Left>
                <Layout.Right>
                    <Button
                        role="delete"
                        onClick={() => {
                            handler.deleteSurvInfo();
                        }}
                    ></Button>
                    <Button
                        role="edit"
                        onClick={() => {
                            navigate(`${URLS.ptli0418003s}/${qestnrId}`);
                        }}
                    ></Button>
                </Layout.Right>
            </Layout>
        </Page>
    );
};
